import { Ros } from "../../foxglove-client";
import type { Publisher, Subscription } from "../../foxglove-client/Impl";
import { DEFAULT_TIMEOUT_MS } from "./consts";
import {
  IControlSource,
  SocketEvent,
  SocketEventHandler,
  Subscriber,
} from "./base";
import { AnyPlainObject } from "../common/misc";
import { ControlSource } from ".";
import { wsAddress } from "../address";

const isPrimitiveType = (value: any) =>
  ["string", "number", "boolean"].includes(typeof value);

const isPrimitiveTypeMsg = (msg: AnyPlainObject) => {
  const keys = Object.keys(msg);
  return keys.length === 1 && keys.includes("data") && isPrimitiveType(msg.data);
};

const unNestDataFieldIfPrimitive = (msg: AnyPlainObject) =>
  isPrimitiveTypeMsg(msg) ? msg.data : msg;

/**
 * Control source backed by foxglove_bridge (Foxglove WebSocket protocol) via the
 * vendored, modified roslibjs-foxglove client.
 *
 * Unlike ROSLibJSControlSource this needs NO rosapi/getTopicType round-trip: the
 * message type comes from the bridge's channel `advertise`. Topic CDR (de)serialization
 * is handled by the vendored client using the advertised schema.
 */
export class FoxgloveControlSource implements IControlSource {
  socket!: Ros;
  private publishers!: { [topic: string]: Publisher<any> };
  private subscribers!: {
    [topic: string]: { handler: Subscriber; sub: Subscription }[];
  };

  constructor(
    host: string,
    {
      // timeoutMs is accepted for parity with the other sources; the vendored
      // client applies its own bounded discovery/connect timeouts.
      timeoutMs = DEFAULT_TIMEOUT_MS,
      port = ControlSource.FOXGLOVE.port,
      useSSL = true,
      token,
      subpath,
    }: {
      timeoutMs?: number;
      /** `null` omits the port — used for same-origin gateway URLs. */
      port?: number | null;
      useSSL?: boolean;
      token?: string;
      /** Path prefix on the gateway, e.g. "api/ros" or "arc/ARC-1100/api/ros". */
      subpath?: string;
    } = {}
  ) {
    void timeoutMs;
    if (typeof host !== "string" || !host)
      throw new Error(`Expected address as non-empty string. Got ${host} instead`);

    const url = wsAddress({ host, port, useSSL, token, subpath });
    this.socket = new Ros({ url });

    this._initialize();
  }

  /** Reuse an already-constructed Ros (shared handle across control/service/action sources). */
  static fromSocket(socket: Ros): FoxgloveControlSource {
    const instance = Object.create(
      FoxgloveControlSource.prototype
    ) as FoxgloveControlSource;
    instance.socket = socket;
    instance._initialize();
    return instance;
  }

  private _initialize(): void {
    this.socket.on("error", (error) => {
      console.error("Error when connecting to foxglove_bridge:", error);
    });
    this.publishers = {};
    this.subscribers = {};
  }

  close() {
    this.socket.close();
  }

  isConnected() {
    return this.socket.isConnected();
  }

  onDisconnect(cb: SocketEventHandler) {
    this.socket.on("close", cb);
  }

  onConnect(cb: SocketEventHandler) {
    this.socket.on("connection", cb);
  }

  onError(cb: SocketEventHandler) {
    this.socket.on("error", cb);
  }

  async send(event: SocketEvent, data: object = {}) {
    const impl = this.socket.rosImpl;
    if (!impl) return;
    try {
      if (!this.publishers[event]) {
        // messageType omitted: derived from the advertised channel schema.
        this.publishers[event] = await impl.createPublisher(event);
      }
      this.publishers[event].publish(
        isPrimitiveType(data) ? { data } : data
      );
    } catch (err) {
      console.error(`Failed to publish to "${event}":`, err);
    }
  }

  async subscribe(event: SocketEvent, handler: Subscriber) {
    const impl = this.socket.rosImpl;
    if (!impl) return;
    try {
      const sub = await impl.createSubscription(event, (msg) =>
        handler(unNestDataFieldIfPrimitive(msg as AnyPlainObject))
      );
      (this.subscribers[event] ??= []).push({ handler, sub });
    } catch (err) {
      console.error(`Failed to subscribe to "${event}":`, err);
    }
  }

  unsubscribe(event: SocketEvent, handler: Subscriber) {
    const list = this.subscribers[event];
    if (!list) return;
    const index = list.findIndex((s) => s.handler === handler);
    if (index < 0) return;
    list[index].sub.unsubscribe();
    list.splice(index, 1);
    if (list.length === 0) delete this.subscribers[event];
  }
}

export type FoxgloveControlSourceArgs = ConstructorParameters<
  typeof FoxgloveControlSource
>;
